import { type AccountOpeningAccountPayload, type AccountOpeningCustomerPayload, type AccountOpeningDTO } from '../../../models/AccountOpening'
import { ITEM_COPY_DEFAULTS } from '../descriptor/stepCopy'
import { type HolderNameSource, type WizardAccount, type WizardContext, type WizardHolder, type WizardParty } from './types'

export const partyKey = (name: string): string => name.trim().toLowerCase()

const namedHolders = (accounts: WizardAccount[]): WizardHolder[] =>
  accounts.flatMap((account) => account.holders).filter((holder) => holder.name.trim().length > 0)

export const reconcileParties = (accounts: WizardAccount[], previous: WizardParty[]): WizardParty[] => {
  const previousByKey = new Map(previous.map((party) => [partyKey(party.name), party]))
  const byKey = new Map<string, WizardParty>()

  namedHolders(accounts).forEach((holder) => {
    const key = partyKey(holder.name)
    const existing = byKey.get(key)
    if (existing) {
      if (!existing.roleCodes.includes(holder.roleCode)) {
        existing.roleCodes.push(holder.roleCode)
      }
      existing.contactId = existing.contactId ?? holder.contactId
      return
    }
    const carried = previousByKey.get(key)
    const roleChanged = !!carried && carried.roleCodes.length > 0 && carried.roleCodes[0] !== holder.roleCode
    byKey.set(key, {
      customerRefId: carried?.customerRefId ?? crypto.randomUUID(),
      name: holder.name.trim(),
      contactId: holder.contactId ?? carried?.contactId ?? null,
      roleCodes: [holder.roleCode],
      kycSchemaSnapshot: roleChanged ? {} : (carried?.kycSchemaSnapshot ?? {}),
      kycValues: carried?.kycValues ?? {},
    })
  })

  return [...byKey.values()]
}

const rolesForAccount = (account: WizardAccount, parties: WizardParty[]): AccountOpeningAccountPayload['roles'] => {
  const partyIdByKey = new Map(parties.map((party) => [partyKey(party.name), party.customerRefId]))
  return account.holders
    .filter((holder) => holder.name.trim().length > 0)
    .map((holder) => ({
      customerRefId: partyIdByKey.get(partyKey(holder.name)) ?? '',
      roleCode: holder.roleCode,
      ...(holder.relationship ? { relationship: holder.relationship } : {}),
    }))
    .filter((role) => role.customerRefId.length > 0)
}

export const buildAccountsPayload = (accounts: WizardAccount[], parties: WizardParty[]): AccountOpeningAccountPayload[] =>
  accounts.map((account) => ({
    accountRefId: account.accountRefId,
    typeId: account.typeId,
    schemaSnapshot: account.schemaSnapshot,
    ...(account.schemaFetchedAt ? { schemaFetchedAt: account.schemaFetchedAt } : {}),
    values: account.values,
    roles: rolesForAccount(account, parties),
  }))

export const partySchemaRole = (party: WizardParty): string | undefined => party.roleCodes[0]

export const buildCustomersPayload = (parties: WizardParty[]): AccountOpeningCustomerPayload[] =>
  parties.map((party) => ({
    customerRefId: party.customerRefId,
    contactId: party.contactId,
    displayName: party.name,
    kycSchemaSnapshot: party.kycSchemaSnapshot,
    kycValues: party.kycValues,
  }))

export const connectorPayload = (ctx: WizardContext, accounts: WizardAccount[], parties: WizardParty[]): Record<string, unknown> => ({
  connector: ctx.connector,
  accounts: buildAccountsPayload(accounts, parties),
  customers: buildCustomersPayload(parties),
})

const savedPartyName = (
  customer: AccountOpeningCustomerPayload,
  index: number,
  resolveContactName: (contactId: string) => string,
  fallbackName: string = ITEM_COPY_DEFAULTS.partyFallbackName,
): string => {
  const contactName = customer.contactId ? resolveContactName(customer.contactId) : ''
  return customer.displayName?.trim() || contactName.trim() || fallbackName.replace('{index}', String(index + 1))
}

const holderNameSource = (party: WizardParty | undefined): HolderNameSource => {
  if (party?.contactId) {
    return 'contact'
  }
  return party?.name.trim() ? 'manual' : null
}

export interface LoadedDraft {
  accounts: WizardAccount[]
  parties: WizardParty[]
}

export const loadDraftIntoWizard = (
  dto: AccountOpeningDTO,
  selectionFor: (typeId: string) => Record<string, string>,
  resolveContactName: (contactId: string) => string,
  fallbackName?: string,
): LoadedDraft => {
  const parties: WizardParty[] = (dto.customers ?? []).map((customer, index) => ({
    customerRefId: customer.customerRefId,
    name: savedPartyName(customer, index, resolveContactName, fallbackName),
    contactId: customer.contactId ?? null,
    roleCodes: [],
    kycSchemaSnapshot: customer.kycSchemaSnapshot ?? {},
    kycValues: customer.kycValues ?? {},
  }))
  const partyById = new Map(parties.map((party) => [party.customerRefId, party]))

  const accounts: WizardAccount[] = (dto.accounts ?? []).map((account) => {
    return {
      accountRefId: account.accountRefId,
      selection: selectionFor(account.typeId),
      typeId: account.typeId,
      holders: (account.roles ?? []).map((role) => {
        const party = partyById.get(role.customerRefId)
        return {
          roleCode: role.roleCode,
          nameSource: holderNameSource(party),
          name: party?.name ?? '',
          contactId: party?.contactId ?? null,
          relationship: role.relationship,
        }
      }),
      schemaSnapshot: account.schemaSnapshot ?? {},
      schemaFetchedAt: account.schemaFetchedAt,
      values: account.values ?? {},
    }
  })

  return { accounts, parties: reconcileParties(accounts, parties) }
}
